import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './MessagesModal.scss';
import { useTranslation, Trans } from 'react-i18next';
import Conversation, {
  resolveViewerMarketplaceRole,
  charsOverMessageLimit,
  messageTooLongText,
} from './Conversation';
import { getAttachmentErrorKey } from './AttachmentPicker';
import { CloseIcon } from '../common/CloseIcon';
import ChatUserAvatar from './ChatUserAvatar';
import VerifiedShieldBadge from '../common/VerifiedShieldBadge/VerifiedShieldBadge';
import StackedAvatars from './StackedAvatars';
import { getCookie } from '../utils/utils';
import { apiRequest } from '../utils/api';
import TagManager from 'react-gtm-module';
import LoadingSpinner from '../common/LoadingSpinner';
import ExpertHelpSheet from './ExpertHelpSheet';
import OffMarketToast from '../common/OffMarketToast/OffMarketToast';
import { useModalStack } from '../../contexts/ModalControllerContext';
import type { OffMarketToastMessage } from '../utils/types';
import {
  savePendingDraftChat,
  clearPendingDraftChat,
  claimPendingDraftSend,
  pendingDraftSendDelivered,
  pendingDraftSendId,
  confirmPendingDraftSend,
} from '../utils/pendingDraftChat';
// pragma: allowlist secret

const DEED_TEMPLATE_URL = 'https://jaakkocom.blob.core.windows.net/jaakkocom-assets/asoy_kauppakirjapohja.docx';

/**
 * Tell the server the user opened or left this chat, so the next message to
 * them may email/SMS them again despite an earlier alert (JAA-438). Never
 * called from the 30 s poll, which keeps an open chat at one alert per
 * cooldown. Best effort: a failed call only means alerts wait out the cooldown.
 */
const rearmChatAlerts = (conversationId: string): void => {
  apiRequest(`/conversations/${conversationId}/rearm-notifications`, {
    method: 'POST',
  }).catch(() => {});
};

/**
 * Applies server order and fresh `read` flags to the messages on screen
 * (JAA-258). Keep existing objects and signed attachment URLs when possible,
 * and return the same array when neither order nor read flags changed.
 */
const withFreshReadFlags = (current: any[], fresh: any[]): any[] => {
  const currentById = new Map(current.map(msg => [msg.id, msg]));
  let changed = current.length !== fresh.length;
  const ordered = fresh.map((msg, index) => {
    const existing = currentById.get(msg.id) ?? msg;
    const updated = existing.read === msg.read ? existing : { ...existing, read: msg.read };
    if (updated !== current[index]) changed = true;
    return updated;
  });
  return changed ? ordered : current;
};

// Icons for the template modal
const DocumentIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="4" width="32" height="40" rx="4" fill="#E8F4FD" stroke="#2196F3" strokeWidth="2" />
    <path d="M14 16H34" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" />
    <path d="M14 24H34" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" />
    <path d="M14 32H26" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" />
    <circle cx="36" cy="36" r="10" fill="#4CAF50" />
    <path d="M32 36L35 39L40 33" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 3V13M10 13L6 9M10 13L14 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 15V16C3 16.5523 3.44772 17 4 17H16C16.5523 17 17 16.5523 17 16V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 10L8 14L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FeatureIcon = ({ type }: { type: 'parties' | 'property' | 'terms' | 'signatures' }) => {
  const icons = {
    parties: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 14s-1 0-1-1 1-4 7-4 7 3 7 4-1 1-1 1H2z" />
      </svg>
    ),
    property: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8.354 1.146a.5.5 0 0 0-.708 0l-6 6A.5.5 0 0 0 1.5 7.5v7a.5.5 0 0 0 .5.5h4.5a.5.5 0 0 0 .5-.5v-4h2v4a.5.5 0 0 0 .5.5H14a.5.5 0 0 0 .5-.5v-7a.5.5 0 0 0-.146-.354L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293L8.354 1.146z" />
      </svg>
    ),
    terms: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z" />
        <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
      </svg>
    ),
    signatures: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z" />
      </svg>
    ),
  };
  return icons[type];
};

type ConversationData = {
  id: string;
  otherUser: {
    name: string;
    avatar: string;
    verified?: boolean;
  };
  lastMessage: {
    text: string;
    timestamp: Date;
    firstMessage: boolean;
    attachments: any[];
  };
  buildingInfo: {
    name: string;
    address: string;
  };
  unread: number;
  isEnded?: boolean;
  endedByUserId?: string;
  endedAt?: Date;
  otherUserHasSentMessage: boolean;
  otherUserRole: 'BUYER' | 'SELLER' | 'UNKNOWN';
  offMarketSellWillingnessId?: string;
  offMarketBuyWillingnessId?: string;
  hasViewedWelcome?: boolean;
  isJaakkoConversation?: boolean;
  participants: Array<{
    userId: string;
    name: string;
    avatar: string | null;
    role: string;
    marketplaceRole?: 'BUYER' | 'SELLER' | null;
    roleLabel?: string;
    isOriginal?: boolean;
    isSelf: boolean;
    addedByCurrentUser?: boolean;
  }>;
  currentUserRole: string;
  currentUserSide?: 'buyer' | 'seller' | null;
  canInvite?: boolean;
  pendingInviteEmail?: string | null;
};

/**
 * A Could-buy click on a listing with no prior conversation opens this
 * client-side draft chat (JAA-275, revised per review): nothing exists on the
 * server and the seller hears nothing until the buyer sends a first message,
 * which POSTs /conversations/could-buy/start and swaps the draft for the real
 * conversation.
 */
export type DraftChatContext = {
  sellWillingnessId: string;
  /** Building address shown as the draft chat's header title. */
  title: string;
  /**
   * Text to prefill the composer with. Set only when AppContent restores a
   * draft the user was writing before they authenticated (JAA-325).
   */
  initialText?: string;
};

type MessagesModalProps = {
  isLandingPage?: boolean;
  /** Closes the modal; wired to the backdrop click. */
  toggleMessagesModal?: () => void;
  conversations: ConversationData[];
  isLoadingConversations?: boolean;
  selectedConversation: string | null;
  setSelectedConversation: (id: string | null) => void;
  refreshConversations: () => void;
  isLoggedIn?: boolean;
  /**
   * Known to be signed out (the login check has resolved). Unlike !isLoggedIn it
   * is false while the check is in flight, so signed-in deep links never flash
   * the signed-out scrim.
   */
  isSignedOut?: boolean;
  onLoginClick?: () => void;
  /** Mobile tab-slide direction (JAA-266): right on forward, left on back. */
  slideDir?: 'forward' | 'back';
  draftChat?: DraftChatContext | null;
  onCloseDraftChat?: () => void;
};

/**
 * Messages surface: the conversation list, the open thread with its 30 s poll
 * and its alert re-arm on open, leave or hidden tab (JAA-438), and the draft
 * chat that creates the conversation on first send (JAA-325).
 */
const MessagesModal: React.FC<MessagesModalProps> = ({
  isLandingPage,
  toggleMessagesModal,
  conversations,
  isLoadingConversations,
  selectedConversation,
  setSelectedConversation,
  refreshConversations,
  isLoggedIn,
  isSignedOut,
  onLoginClick,
  slideDir = 'forward',
  draftChat,
  onCloseDraftChat,
}) => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  // Per-conversation drafts. Keyed by conversation id. Switching conversations
  // no longer leaks unsent text; returning to a conversation restores its draft.
  // Cleared for a given conv once the send is successful (see handleSendMessage).
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const newMessage = selectedConversation
    ? (drafts[selectedConversation] ?? '')
    : '';

  const setNewMessage = useCallback(
    (textOrUpdater: string | ((prev: string) => string)) => {
      if (!selectedConversation) return;
      setDrafts(prev => {
        const current = prev[selectedConversation] ?? '';
        const next =
          typeof textOrUpdater === 'function'
            ? (textOrUpdater as (p: string) => string)(current)
            : textOrUpdater;
        if (next === current) return prev; // skip no-op update
        return { ...prev, [selectedConversation]: next };
      });
    },
    [selectedConversation],
  );
  const [showEndChatConfirmation, setShowEndChatConfirmation] = useState(false);
  const [isEndingChat, setIsEndingChat] = useState(false);
  const [showParticipantPanel, setShowParticipantPanel] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showExpertHelp, setShowExpertHelp] = useState(false);
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading' | 'success'>('idle');
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const [showHelpMenu, setShowHelpMenu] = useState(false);

  // Handle template download
  const handleDownloadTemplate = async () => {
    setDownloadState('downloading');

    try {
      // Fetch the file
      const response = await fetch(DEED_TEMPLATE_URL);
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'kauppakirjapohja.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDownloadState('success');

      // Track the download
      TagManager.dataLayer({
        dataLayer: {
          event: 'deed_template_downloaded',
          category: 'User Interaction',
          action: 'Download',
        },
      });

      // Reset after showing success
      setTimeout(() => {
        setDownloadState('idle');
      }, 2500);
    } catch (error) {
      console.error('Error downloading template:', error);
      setDownloadState('idle');
    }
  };

  // Format timestamp to readable format
  const formatTimestamp = (timestamp: Date) => {
    // Check if timestamp is valid
    if (!timestamp || isNaN(timestamp.getTime())) {
      console.warn('Invalid timestamp in formatTimestamp:', timestamp);
      return '';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate());

    // Get user's locale from browser
    const userLocale = i18n.language || 'en-GB';

    // Check if the message is from today
    if (messageDate.getTime() === today.getTime()) {
      return new Intl.DateTimeFormat(userLocale, {
        hour: '2-digit',
        minute: '2-digit'
      }).format(timestamp);
    }

    // Check if the message is from within this week
    const dayDiff = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));
    if (dayDiff < 7) {
      return new Intl.DateTimeFormat(userLocale, {
        weekday: 'short'
      }).format(timestamp);
    }

    // For older messages, show date
    return new Intl.DateTimeFormat(userLocale, {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    }).format(timestamp);
  };

  /**
   * Loads a conversation's messages. The server marks the other side's
   * messages read as a side effect, and returns `read` on the viewer's own
   * messages in a 1:1 chat (JAA-258), which the thread turns into Sent/Seen.
   */
  const selectedConversationRef = useRef(selectedConversation);
  useLayoutEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  const messageFetchSequence = useRef(0);
  const messageAppliedSequence = useRef(0);
  const fetchMessages = useCallback(async (conversationId: string, showLoading = true): Promise<void> => {
    if (conversationId !== selectedConversationRef.current) return;
    const requestSequence = ++messageFetchSequence.current;
    if (!isLoggedIn) {
      setMessages([]);
      setIsLoadingMessages(false);
      return;
    }

    // Only show loading indicator on initial load or manual refresh, not during polling
    if (showLoading) {
      setIsLoadingMessages(true);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/conversations/${conversationId}/messages`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      if (conversationId !== selectedConversationRef.current || requestSequence < messageAppliedSequence.current) return;
      messageAppliedSequence.current = requestSequence;
      setIsLoadingMessages(false);

      // Transform the data to match your component's expected format
      const formattedMessages = data.map((msg: any) => {
        // Validate timestamp before creating Date object
        let timestamp;
        try {
          timestamp = new Date(msg.timestamp);
          // Check if the date is valid
          if (isNaN(timestamp.getTime())) {
            console.warn('Invalid timestamp in message:', msg);
            timestamp = new Date(); // Fallback to current date
          }
        } catch (error) {
          console.error('Error parsing timestamp:', error, msg);
          timestamp = new Date(); // Fallback to current date
        }

        return {
          id: msg.id || `temp-${Date.now()}-${Math.random()}`,
          sender: msg.sender,
          text: msg.text || '',
          timestamp,
          attachments: msg.attachments || undefined,
          tipData: msg.tipData || undefined,
          senderId: msg.senderId || undefined,
          senderName: msg.senderName || undefined,
          systemEvent: msg.systemEvent || undefined,
          read: msg.read
        };
      });

      // Use the functional form of setState to access the current messages
      setMessages(currentMessages => {
        // Compare the new messages with current messages
        const currentIds = new Set(currentMessages.map(msg => msg.id));
        const hasNewMessages = formattedMessages.some(msg => !currentIds.has(msg.id));

        // Only update if there are new messages
        if (hasNewMessages || currentMessages.length !== formattedMessages.length) {
          return formattedMessages;
        }
        // Same messages, but a read flag may have flipped (re-renders only then)
        return withFreshReadFlags(currentMessages, formattedMessages);
      });
    } catch (error) {
      console.error('Error fetching messages:', error);
      // Handle error (show notification, etc.)
    } finally {
      // The newest completed request also releases any initial-load spinner.
      if (requestSequence === messageFetchSequence.current) {
        setIsLoadingMessages(false);
      }
    }
  }, [isLoggedIn]);

  // Polling: when a conversation is selected, fetch both messages and conversations
  // on the same interval so they stay in sync. The list is only refreshed for
  // its previews and last-message ordering — the unread count of the open chat
  // is already zeroed locally (AppContent), so nothing on screen waits for it.
  // When no conversation is selected, AppContent handles conversation polling.
  useEffect(() => {
    if (!selectedConversation || !isLoggedIn) return;

    const fetchData = async () => {
      const showLoading = !skipMessagesLoadingRef.current;
      skipMessagesLoadingRef.current = false;
      await fetchMessages(selectedConversation, showLoading);
      refreshConversations();
    }
    fetchData();

    const interval = setInterval(() => {
      fetchMessages(selectedConversation, false);
      refreshConversations();
    }, 30000);

    return () => {
      clearInterval(interval);
      messageFetchSequence.current += 1;
      messageAppliedSequence.current = messageFetchSequence.current;
    };
  }, [selectedConversation, isLoggedIn, fetchMessages, refreshConversations]);

  /**
   * Opening a chat, leaving it (another chat, the list, closing Messages) and
   * hiding the tab re-arm the user's email/SMS alerts; the poll above never
   * does (JAA-438). Its own effect, keyed on the chat and login only, so a
   * changed callback identity in the polling effect can never re-arm.
   */
  useEffect(() => {
    if (!selectedConversation || !isLoggedIn) return;
    rearmChatAlerts(selectedConversation);

    /** Hiding the tab (or locking the phone) counts as leaving the chat. */
    const rearmWhenHidden = () => {
      if (document.visibilityState === 'hidden') {
        rearmChatAlerts(selectedConversation);
      }
    };
    document.addEventListener('visibilitychange', rearmWhenHidden);

    return () => {
      document.removeEventListener('visibilitychange', rearmWhenHidden);
      rearmChatAlerts(selectedConversation);
    };
  }, [selectedConversation, isLoggedIn]);

  /**
   * Sends the composer text, then refreshes authoritative receipt eligibility.
   * Optimistic rows never claim a read status in a Jaakko or former group
   * conversation (JAA-258). Resolves true when the message went out.
   */
  const sendMessage = async (): Promise<boolean> => {
    if (!newMessage.trim() || !selectedConversation) return false;

    try {
      // Show some loading state if needed
      // setIsSending(true);

      // Send to your API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/conversations/${selectedConversation}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify({ text: newMessage }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      // Parse the response
      const messageData = await response.json();

      if (selectedConversationRef.current === selectedConversation) {
        // A pre-send poll cannot know about this accepted message.
        messageAppliedSequence.current = ++messageFetchSequence.current;

        // Add message to the local state
        setMessages(prev => [...prev, {
          id: messageData.id,
          sender: messageData.sender,
          text: messageData.text,
          timestamp: new Date(messageData.timestamp),
          attachments: messageData.attachments || undefined,
          read: undefined
        }]);
      }

      // Clear the input
      setNewMessage('');
      // Ask the server for receipt eligibility, including former-group history.
      void fetchMessages(selectedConversation, false);

      // After sending, refresh the conversations list to update last message
      setTimeout(() => {
        refreshConversations();
      }, 300);

      TagManager.dataLayer({
        dataLayer: {
          event: 'chat_message_sent',
          category: 'User Interaction',
          action: 'Click',
        },
      });
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      // Handle error (show notification, etc.)
      return false;
    } finally {
      // setIsSending(false);
    }
  };

  // Function to handle ending the chat
  const handleEndChat = () => {
    setShowEndChatConfirmation(true);
  };

  // Function to confirm ending the chat
  const confirmEndChat = async () => {
    if (!selectedConversation) return;

    setIsEndingChat(true);
    try {
      // Call API to end the chat
      const response = await fetch(`${process.env.REACT_APP_API_URL}/conversations/${selectedConversation}/end`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to end conversation');
      }

      // Go back to conversation list and refresh conversations
      setSelectedConversation(null);
      refreshConversations();

    } catch (error) {
      console.error('Error ending chat:', error);
      // Could add error handling UI here
    } finally {
      setIsEndingChat(false);
      setShowEndChatConfirmation(false);
    }
  };

  // Function to cancel ending the chat
  const cancelEndChat = () => {
    setShowEndChatConfirmation(false);
  };

  // Draft-chat first send (JAA-275, revised per review): this is the moment the
  // conversation comes into being — the endpoint creates it, stores the message
  // and notifies the seller. On success the draft swaps for the real
  // conversation and the normal message flow takes over.
  const [draftText, setDraftText] = useState('');
  const [isSendingDraft, setIsSendingDraft] = useState(false);
  // The restored draft whose text is already in the composer; until then the text lives only in initialText.
  const prefilledDraftRef = useRef<DraftChatContext | null>(null);
  // Seller of the last draft shown, so a draft reopening for the same seller keeps its typed text.
  const lastDraftSellerRef = useRef<string | null>(null);

  /**
   * Prefill the composer when a restored draft arrives, and empty it when a
   * draft for another seller replaces it: text typed for one seller must never
   * carry over to another seller's draft (JAA-445). Keyed on the draft's
   * identity, not on draftChat itself, so a re-render never overwrites what
   * the user is currently typing.
   */
  useEffect(() => {
    if (!draftChat) return;
    if (draftChat.initialText) {
      setDraftText(draftChat.initialText);
      prefilledDraftRef.current = draftChat;
    } else if (lastDraftSellerRef.current !== draftChat.sellWillingnessId) {
      setDraftText('');
    }
    lastDraftSellerRef.current = draftChat.sellWillingnessId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftChat?.sellWillingnessId, draftChat?.initialText]);
  // Why a draft send was refused. Uses the shared off-market toast so the
  // message reads the same here as on the building page and explore cards.
  const [toastMessage, setToastMessage] = useState<OffMarketToastMessage | string | null>(null);
  // Set for the draft -> real conversation handover only. Without it the
  // selectedConversation effect refetches with the loading indicator on, so the
  // pane the user just typed into blanks out and reappears.
  const skipMessagesLoadingRef = useRef(false);
  // Stand-in record for the conversation the draft just created. The list is
  // refreshed asynchronously, so without this the view has an id it cannot
  // resolve and falls through to a blank spinner. The real record wins as soon
  // as the refresh lands.
  const [pendingConversation, setPendingConversation] = useState<ConversationData | null>(null);

  // The optimistic record has to reach the LIST as well as the conversation
  // view, or going straight back after the first send shows a list without the
  // chat until /conversations lands — measured at ~2.7s on a heavy account
  // (JAA-275). The real record replaces it as soon as the refetch commits.
  const filteredConversations = useMemo(
    () =>
      pendingConversation &&
      !conversations.some(c => c.id === pendingConversation.id)
        ? [pendingConversation, ...conversations]
        : conversations,
    [conversations, pendingConversation],
  );

  /** True when the open draft began signed out (opened signed out, or restored after a login). */
  const draftBeganSignedOutRef = useRef(false);

  // The draft (by sellWillingnessId) whose composer shows the "not sent yet" hint.
  const [notSentHintFor, setNotSentHintFor] = useState<string | null>(null);
  // Set once a conversation fetch starts while signed in, so the hint waits for that list.
  const listFetchSeenSignedInRef = useRef(false);
  // The draft this modal already sent on login (JAA-447); the hint and the re-match leave it alone.
  const autoSentDraftRef = useRef<DraftChatContext | null>(null);
  // Prevent re-matching while the cross-tab claim is being resolved.
  const claimingDraftRef = useRef<DraftChatContext | null>(null);
  const [isClaimingDraft, setIsClaimingDraft] = useState(false);
  const autoSendContextRef = useRef({ draftChat, isLoggedIn, isSendingDraft, text: '' });
  autoSendContextRef.current = {
    draftChat,
    isLoggedIn,
    isSendingDraft,
    text:
      draftChat?.initialText && prefilledDraftRef.current !== draftChat
        ? draftChat.initialText
        : draftText,
  };

  /** The draft's text as the user sees it, including a restored text the prefill has not applied yet. */
  const currentDraftText = () => {
    if (!draftChat || prefilledDraftRef.current === draftChat) return draftText;
    return draftText || draftChat.initialText || '';
  };

  /** Record, per opened draft, whether it began signed out. */
  useEffect(() => {
    draftBeganSignedOutRef.current =
      !!draftChat && (!isLoggedIn || !!draftChat.initialText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftChat]);

  /**
   * Once a draft that began signed out is signed in and the conversation list
   * fetched after that login has arrived, show a hint above its composer that
   * nothing has been sent yet, so the kept text reads as waiting for Send rather
   * than as delivered (JAA-325). Waiting for the list lets the re-match below
   * swap to an existing thread first. No hint when that list shows an ended
   * thread with this seller: that Send is refused with its own message.
   * A draft already sent on login (JAA-447) gets no hint: it either became the
   * real conversation or its refusal toast says why it was not sent.
   */
  useEffect(() => {
    if (!draftChat) {
      setNotSentHintFor(null);
      listFetchSeenSignedInRef.current = false;
      return;
    }
    if (autoSentDraftRef.current === draftChat || claimingDraftRef.current === draftChat) return;
    if (!isLoggedIn || !draftBeganSignedOutRef.current) return;
    if (isLoadingConversations) {
      listFetchSeenSignedInRef.current = true;
      return;
    }
    if (!listFetchSeenSignedInRef.current || !currentDraftText().trim()) return;
    const endedThread = conversations.some(
      (c) =>
        c.offMarketSellWillingnessId === draftChat.sellWillingnessId &&
        c.currentUserSide === 'buyer' &&
        c.isEnded,
    );
    if (!endedThread) setNotSentHintFor(draftChat.sellWillingnessId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, draftChat, isLoadingConversations, isClaimingDraft]);

  /**
   * Send a draft parked at the Send gate as soon as the user is signed in, so
   * logging in or registering from Ask more never needs a second Send
   * (JAA-447). Covers the in-page logins, where this modal stays mounted, and a
   * signed-in restore after a reload or in the iOS Google tab.
   * claimPendingDraftSend allows one attempt per press of Send across
   * re-renders, reloads and tabs, and hands back the text that was parked. Only
   * the text the user pressed Send with goes out: a composer edited or emptied
   * since holds a text nobody confirmed, so it is left for an explicit Send (the
   * press is used up, and the parked text still restores on reload). The latest
   * composer context includes a restored prefill and is checked again after the
   * asynchronous claim, so edits, logout and closing the draft still cancel it.
   * A refused or failed send keeps the text and toasts why, exactly as a pressed
   * Send does; the existing thread with this
   * seller, if any, receives it server-side.
   */
  useEffect(() => {
    if (!isLoggedIn || !draftChat || isSendingDraft) return;
    let active = true;
    claimingDraftRef.current = draftChat;
    setIsClaimingDraft(true);
    claimPendingDraftSend(draftChat.sellWillingnessId)
      .then((claim) => {
        const current = autoSendContextRef.current;
        if (
          !active || !claim || !current.isLoggedIn || current.isSendingDraft ||
          current.draftChat !== draftChat || current.text.trim() !== claim.text.trim()
        ) return;
        autoSentDraftRef.current = draftChat;
        return sendDraftMessage([], claim.text, claim.id);
      })
      .finally(() => {
        if (!active) return;
        claimingDraftRef.current = null;
        setIsClaimingDraft(false);
      });
    return () => {
      active = false;
      if (claimingDraftRef.current === draftChat) claimingDraftRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, draftChat]);

  /**
   * Swap a draft that began signed out for the buyer's existing, open thread with
   * this seller once the conversation list shows one (after an in-page login, a
   * signed-in restore or a poll), carrying the typed text into that thread's
   * composer (JAA-325). Never into a thread where the user sells, and never into
   * an ended one, where the text could not be sent: that Send is refused with
   * its own message instead. The text is carried unless this intent has a
   * recorded successful response, including one received by the copied Google
   * tab. A claimed attempt or equal old message does not prove delivery: after
   * failure or an unknown outcome the text must remain available. From then on
   * it is that thread's unsent
   * composer text like any other chat's, so the parked copy goes.
   * Not for a draft already sent on login (JAA-447): the server put that message
   * into the existing thread itself, and copying the text there as well would
   * invite a second, duplicate Send.
   */
  useEffect(() => {
    if (
      !draftChat ||
      isSendingDraft ||
      claimingDraftRef.current === draftChat ||
      !draftBeganSignedOutRef.current ||
      autoSentDraftRef.current === draftChat
    ) {
      return;
    }
    const existing = conversations.find(
      (c) =>
        c.offMarketSellWillingnessId === draftChat.sellWillingnessId &&
        c.currentUserSide === 'buyer' &&
        !c.isEnded,
    );
    if (!existing) return;
    const text = currentDraftText();
    const delivered = pendingDraftSendDelivered(draftChat.sellWillingnessId, text);
    if (text.trim() && !delivered) {
      setDrafts((prev) => ({ ...prev, [existing.id]: text }));
    }
    setDraftText('');
    onCloseDraftChat?.();
    setSelectedConversation(existing.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, draftChat, isSendingDraft, isClaimingDraft]);


  /**
   * Park the unsent draft and send the visitor to authentication (JAA-325).
   * Wired to the signed-out draft composer's onUnauthenticatedSend, so a
   * signed-out send never reaches the server. The parked copy restores the text
   * if the login costs this tab its React state (a reload, the iOS popup tab).
   */
  const promptLoginForDraft = () => {
    if (draftChat && draftText.trim()) {
      savePendingDraftChat({
        sellWillingnessId: draftChat.sellWillingnessId,
        title: draftChat.title,
        text: draftText,
      });
    }
    // Otherwise the consent modal opens 600ms after login, over this chat.
    try {
      sessionStorage.setItem('marketing_consent_deferred', '1');
    } catch {
      // Storage blocked: login must still open; the consent prompt may then follow it.
    }
    onLoginClick?.();
  };

  /**
   * Send the draft chat's first message via POST /conversations/could-buy/start
   * and swap the draft for the real conversation. Called by the composer's Send
   * and, once per parked Send, by the login auto-send (JAA-447), which passes the
   * parked text for a restored draft whose prefill may not have reached
   * draftText yet.
   * When that send created the conversation (a new Ask more match), also push
   * the buy submit event the container's buyer ad conversion listens to
   * (JAA-385). A refusal toasts its reason; one that can never succeed (own
   * listing, selling in the building, ended thread, listing gone) also drops the
   * parked copy. Resolves true only when the server accepted the message. A text
   * over the 5,000-character limit (an older one restored after login can be)
   * is refused here and by the server alike with the same message, never
   * truncated and never cleared, so the user can shorten it and send (JAA-438).
   */
  const sendDraftMessage = async (
    files: File[] = [],
    text: string = draftText,
    claimedSendId: string | null = null,
  ): Promise<boolean> => {
    if (
      !draftChat ||
      (!text.trim() && files.length === 0) ||
      isSendingDraft
    ) {
      return false;
    }
    const over = charsOverMessageLimit(text);
    if (over > 0) {
      setToastMessage(messageTooLongText(t, i18n.language, over));
      return false;
    }
    const sentText = text.trim();
    const sendIntentId =
      claimedSendId || pendingDraftSendId(draftChat.sellWillingnessId, sentText);
    setToastMessage(null);
    setIsSendingDraft(true);
    try {
      const formData = new FormData();
      formData.append('sell_willingness_id', draftChat.sellWillingnessId);
      formData.append('text', sentText);
      files.forEach(file => formData.append('files', file));
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/conversations/could-buy/start`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: formData,
        },
      );
      if (!res.ok) {
        // The server is the first place either surface learns the send was
        // refused — an explore card cannot know the viewer sells in that
        // building, and neither knows their contact quota. Say which it was
        // instead of failing silently.
        const err = await res.json().catch(() => ({} as { error_code?: string }));
        setNotSentHintFor(null);
        // A refusal that can never succeed must not come back with the next reload.
        if (
          res.status === 404 ||
          err.error_code === 'USER_SELLER_AND_BUYER' ||
          err.error_code === 'SELF_CONVERSATION' ||
          err.error_code === 'CONVERSATION_ENDED'
        ) {
          clearPendingDraftChat();
        }
        if (err.error_code === 'USER_SELLER_AND_BUYER') {
          setToastMessage('USER_SELLER_AND_BUYER');
        } else if (err.error_code === 'COULD_BUY_LIMIT_REACHED') {
          setToastMessage('COULD_BUY_LIMIT_REACHED');
        } else if (err.error_code === 'SELF_CONVERSATION') {
          setToastMessage(t('messages.ownListing'));
        } else if (err.error_code === 'CONVERSATION_ENDED') {
          setToastMessage(t('messages.chatEndedMessage'));
        } else if (err.error_code === 'MESSAGE_TOO_LONG') {
          setToastMessage(
            messageTooLongText(
              t,
              i18n.language,
              Math.max(1, charsOverMessageLimit(text)),
            ),
          );
        } else if (res.status === 404) {
          setToastMessage(t('messages.listingRemoved.title'));
        } else {
          setToastMessage(
            t(
              getAttachmentErrorKey(err.error) ?? 'common.error',
              'An error occurred. Please try again.',
            ),
          );
        }
        return false;
      }
      const data: {
        conversation_id: string;
        created?: boolean;
        message?: {
          id: string;
          sender: 'self';
          text: string;
          timestamp: string;
          attachments?: any[];
        };
      } = await res.json();
      if (typeof data.conversation_id === 'string' && data.conversation_id) {
        confirmPendingDraftSend(sendIntentId);
      }
      const sentMessage = data.message || {
        id: data.conversation_id,
        sender: 'self' as const,
        text: sentText,
        timestamp: new Date().toISOString(),
        attachments: [],
      };
      const sentAt = new Date(sentMessage.timestamp);
      // Seed the message that was just sent so the conversation opens already
      // showing it; the background refetch below replaces it with the stored
      // copy without the view ever being empty.
      setMessages([
        {
          id: sentMessage.id,
          sender: sentMessage.sender,
          text: sentMessage.text,
          timestamp: sentAt,
          attachments: sentMessage.attachments || undefined,
        },
      ]);
      skipMessagesLoadingRef.current = true;
      setPendingConversation({
        id: data.conversation_id,
        otherUser: { name: '', avatar: '' },
        lastMessage: {
          text: sentMessage.text,
          timestamp: sentAt,
          firstMessage: false,
          attachments: sentMessage.attachments || [],
        },
        buildingInfo: { name: 'Apartment', address: draftChat.title },
        unread: 0,
        otherUserHasSentMessage: false,
        otherUserRole: 'SELLER',
        offMarketSellWillingnessId: draftChat.sellWillingnessId,
        currentUserRole: 'member',
        currentUserSide: 'buyer',
        participants: [
          {
            userId: '',
            name: '',
            avatar: null,
            role: '',
            marketplaceRole: 'SELLER',
            roleLabel: 'seller',
            isSelf: false,
          },
        ],
      });
      setDraftText('');
      clearPendingDraftChat();
      onCloseDraftChat?.();
      setSelectedConversation(data.conversation_id);
      refreshConversations();
      TagManager.dataLayer({
        dataLayer: {
          event: 'chat_message_sent',
          category: 'User Interaction',
          action: 'Click',
        },
      });
      // Only a new match converts; the container's buyer conversion listens to buy_submit_building_modal|landing_page, so Ask more reuses that event.
      if (data.created) {
        TagManager.dataLayer({
          dataLayer: {
            event: 'buy_submit_building_modal',
            category: 'User Interaction',
            action: 'Click',
            label: 'Ask More First Message',
          },
        });
      }
      return true;
    } catch (err) {
      console.error('Error starting draft conversation:', err);
      setToastMessage(t('common.error', 'An error occurred. Please try again.'));
      return false;
    } finally {
      setIsSendingDraft(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target as Node)) {
        setShowHelpMenu(false);
      }
    }

    if (showHelpMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHelpMenu]);

  // Whether another page is layered UNDER Messages on the modal stack — the
  // building view (JAA-275) or the listing route. Both hide the bottom nav, so
  // there the close button is the only way back to that page and has to stay.
  // As the bottom layer Messages sits straight on the landing page, or on an
  // Activity view it covers (JAA-443), which the nav and the backdrop already
  // return to.
  const modalStack = useModalStack();
  const hasPageBelow = modalStack.findIndex(entry => entry.id === 'dms') > 0;

  return (
    <div
      className={`messages-modal-background ${isLandingPage ? 'messages-modal-background--landing-page' : ''}${hasPageBelow ? ' messages-modal-background--has-page-below' : ''}${isSignedOut ? ' messages-modal-background--signed-out' : ''}`}
      onClick={(e) => {
        // Backdrop only — ignore clicks bubbling out of the modal panel.
        if (e.target === e.currentTarget) toggleMessagesModal?.();
      }}
    >
      <div
        className={`messages-modal ${isLandingPage ? 'messages-modal--landing-page' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('newLandingPage.nav.messages')}
        data-slide={slideDir}
        key={selectedConversation ? 'selected-conversation-open' : 'no-selected-conversation-open'}
      >
        {!selectedConversation && !draftChat && (
          <div className="messages-modal__header">
            <h2>
              <Trans i18nKey="messages.title" />
            </h2>
            {/* Explicit close for the list view, kept only where the page
                underneath cannot be reached any other way. Rendered in the
                title row via the pre-existing absolute-positioned __close
                style, so dropping it leaves the centred title untouched. */}
            {toggleMessagesModal && hasPageBelow && (
              <button
                type="button"
                className="messages-modal__close"
                aria-label={t('common.close', 'Close')}
                onClick={toggleMessagesModal}
              >
                <CloseIcon size={13} color="#1a1a1a" />
              </button>
            )}
          </div>
        )}

        {/* List-level toolbar. The draft pane also renders with no selected
            conversation, so it has to be excluded explicitly or this chrome
            stacks on top of the draft's own header. */}
        {!selectedConversation && !draftChat && (
          <div className="messages-modal__tabs">
            <div className="messages-modal__tabs__help-container" ref={helpMenuRef}>
              <button
                className="messages-modal__tabs__action-button"
                onClick={() => { setShowHelpMenu(!showHelpMenu); }}
              >
                <Trans i18nKey="messages.helpWithDeal">Help with the deal</Trans>
              </button>
              {showHelpMenu && (
                <div className="messages-modal__tabs__help-menu">
                  <button
                    className="messages-modal__tabs__menu-item"
                    onClick={() => {
                      setShowHelpMenu(false);
                      window.open(t('messages.selfSellingGuideUrl'), '_blank', 'noopener');
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="m17.5 11c-3.584 0-6.5 2.916-6.5 6.5s2.916 6.5 6.5 6.5 6.5-2.916 6.5-6.5-2.916-6.5-6.5-6.5zm1 10c0 .552-.447 1-1 1s-1-.448-1-1v-3.5c0-.552.447-1 1-1s1 .448 1 1zm-1-6c-.552 0-1-.448-1-1s.448-1 1-1 1 .448 1 1-.448 1-1 1z" />
                      <path d="m17.98.11c-2.59-.38-4.88.24-6.98 1.89-2.1-1.65-4.39-2.27-6.98-1.89-.87.12-1.52.87-1.52 1.73v10.97c0 .49.21.96.58 1.29.37.34.88.5 1.38.44 1.59-.18 3.13.15 4.55.96.07.03.13.07.2.11.01-.04.02-.07.03-.11.11-.46.26-.91.45-1.34-1.37-.77-2.85-1.17-4.38-1.17-.34 0-.67.02-1.02.06-.11.01-.18-.04-.21-.07-.05-.04-.08-.1-.08-.17v-10.97c0-.12.1-.23.23-.25 2.26-.33 4.18.23 6.02 1.74.09.06.17.14.26.21.28.25.7.25.98 0 .09-.07.17-.15.26-.21 1.84-1.51 3.76-2.06 6.02-1.74.13.02.23.13.23.25v7.17c.51.03 1.01.11 1.5.23v-7.4c0-.86-.65-1.61-1.52-1.73z" />
                      <path d="m9.24 15.5c-.01.04-.02.07-.03.11-.1.45-.17.92-.2 1.39h-7.26c-.96 0-1.75-.79-1.75-1.75v-11.5c0-.96.78-1.74 1.74-1.75h1.26c.41 0 .75.33.75.75 0 .41-.33.75-.75.75h-1.25c-.14 0-.25.12-.25.25v11.5c0 .14.11.25.25.25z" />
                      <path d="m22 3.75v6.54c-.47-.29-.97-.54-1.5-.74v-5.8c0-.14-.11-.25-.25-.25h-1.25c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h1.25c.96 0 1.75.79 1.75 1.75z" />
                      <path d="m11.75 3v8.25c-.58.53-1.09 1.14-1.5 1.82v-10.07c0-.41.34-.75.75-.75s.75.34.75.75z" />
                    </svg>
                    <Trans i18nKey="messages.selfSellingGuide">Self-selling guide</Trans>
                  </button>
                  <button
                    className="messages-modal__tabs__menu-item"
                    onClick={() => { setShowHelpMenu(false); setShowTemplateModal(true); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.5 1.5H4.5C3.67157 1.5 3 2.17157 3 3V15C3 15.8284 3.67157 16.5 4.5 16.5H13.5C14.3284 16.5 15 15.8284 15 15V6L10.5 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10.5 1.5V6H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9 13.5V9M9 9L6.75 11.25M9 9L11.25 11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <Trans i18nKey="messages.loadTemplate">Deed of sale template</Trans>
                  </button>
                  <button
                    className="messages-modal__tabs__menu-item"
                    onClick={() => { setShowHelpMenu(false); setShowExpertHelp(true); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <Trans i18nKey="messages.getPersonalHelp">Get personal help</Trans>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <OffMarketToast message={toastMessage} onDismiss={() => setToastMessage(null)} placement="top" />
        <div className="messages-modal__content">
          {draftChat && !selectedConversation ? (
            // Draft chat (JAA-275): rendered by the real Conversation component
            // with an empty message list, so it is pixel-identical to opening a
            // conversation that has no messages yet — welcome block, listing
            // card and first-message suggestions all included. Nothing exists
            // server-side; sendDraftMessage creates it on the first send.
            <Conversation
              conversationId=""
              conversation={{
                id: '',
                otherUser: { name: '', avatar: '' },
                buildingInfo: { name: 'Apartment', address: draftChat.title },
                // The header's avatar and title both render from the non-self
                // participants, so the seller has to be present here or the
                // draft shows an empty avatar slot and a blank <h3>. No name:
                // that falls through to the anonymous "Interested seller"
                // label, which is all the buyer may see before they write.
                participants: [
                  {
                    userId: '',
                    name: '',
                    avatar: null,
                    role: '',
                    marketplaceRole: 'SELLER',
                    roleLabel: 'seller',
                    isSelf: false,
                  },
                ],
              }}
              messages={[]}
              isLoading={false}
              newMessage={draftText}
              otherUserRole="SELLER"
              setNewMessage={setDraftText}
              sendMessage={sendDraftMessage}
              onUnauthenticatedSend={isLoggedIn ? undefined : promptLoginForDraft}
              composerHint={
                notSentHintFor === draftChat.sellWillingnessId
                  ? t('messages.draftNotSentYet')
                  : undefined
              }
              // Signed out, the list behind the draft is empty with no way out, so back closes Messages.
              goBack={
                isLoggedIn
                  ? () => onCloseDraftChat?.()
                  : () => toggleMessagesModal?.()
              }
              offMarketSellWillingnessId={draftChat.sellWillingnessId}
              currentUserSide="buyer"
              hasViewedWelcome={false}
              refreshConversations={refreshConversations}
              showParticipantPanel={false}
              setShowParticipantPanel={() => {}}
              showInviteModal={false}
              setShowInviteModal={() => {}}
              onShowTemplate={() => setShowTemplateModal(true)}
              // Expert help needs an account; signed out the draft hides it rather than lose a typed contact at login.
              onShowExpertHelp={isLoggedIn ? () => setShowExpertHelp(true) : undefined}
            />
          ) : !selectedConversation ? (
            // Conversation list view
            <div className="messages-modal__conversations">
              {isLoadingConversations && filteredConversations.length === 0 ? (
                <div className="messages-modal__loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <LoadingSpinner />
                </div>
              ) : filteredConversations.length === 0 ? (
                <>
                  <div className="messages-modal__empty">
                    <Trans i18nKey="messages.empty">
                      No messages yet
                    </Trans>
                  </div>
                  {!isLoggedIn && onLoginClick && (
                    <div className="messages-modal__login-button-container">
                      <button
                        className="messages-modal__login-button"
                        onClick={onLoginClick}
                      >
                        <Trans i18nKey="signupLoginModal.loginOrSignup">
                          Login or sign up
                        </Trans>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                filteredConversations.map(conversation => (
                  <div
                    key={conversation.id}
                    className={`messages-modal__conversation ${conversation.unread > 0 ? 'messages-modal__conversation--unread' : ''} ${conversation.isEnded ? 'messages-modal__conversation--ended' : ''}`}
                    onClick={() => {
                      setSelectedConversation(conversation.id);
                    }}
                  >
                    <div className="messages-modal__conversation-avatar">
                      {conversation.participants && conversation.participants.length > 2 ? (
                        <StackedAvatars
                          participants={conversation.participants.filter(p => !p.isSelf)}
                          maxVisible={3}
                          size={36}
                        />
                      ) : conversation.otherUser.avatar ? (
                        <span style={{ position: 'relative', display: 'inline-block' }}>
                          <img src={conversation.otherUser.avatar} alt={conversation.otherUser.name} />
                          {conversation.otherUser.verified && <VerifiedShieldBadge size="sm" />}
                        </span>
                      ) : (
                        <ChatUserAvatar
                          name={conversation.otherUserHasSentMessage ? conversation.otherUser.name : ""}
                          verified={!!conversation.otherUser.verified}
                        />
                      )}
                    </div>
                    <div className="messages-modal__conversation-content">
                      <div className="messages-modal__conversation-header">
                        <h3>{conversation.participants && conversation.participants.length > 2
                          ? conversation.participants.filter(p => !p.isSelf).map(p =>
                              p.name || (p.roleLabel === 'seller' ? t('messages.seller') : p.roleLabel === 'buyer' ? t('messages.buyer') : p.roleLabel === 'seller_participant' ? t('messages.sellerParticipant') : p.roleLabel === 'buyer_participant' ? t('messages.buyerParticipant') : t('messages.participants.participant'))
                            ).join(', ')
                          : conversation.otherUserHasSentMessage ? conversation.otherUser.name : conversation.otherUserRole === 'BUYER' ? t("messages.buyer") : t("messages.seller")}</h3>
                        <span className="messages-modal__conversation-time">
                          {formatTimestamp(conversation.lastMessage.timestamp)}
                        </span>
                      </div>
                      <div className="messages-modal__conversation-content-bottom">
                        <div className="messages-modal__conversation-content-bottom-left">
                          <div className="messages-modal__conversation-building">
                            {conversation.buildingInfo.address}
                          </div>
                          <div className="messages-modal__conversation-preview">
                            {conversation.isEnded ? (
                              <span className="messages-modal__conversation-ended-tag">
                                <Trans i18nKey="messages.chatEnded">Chat ended</Trans>
                              </span>
                            ) : conversation.lastMessage.firstMessage ? (
                              <Trans i18nKey="messages.firstMessage" />
                            ) : (() => {
                              const lm: any = conversation.lastMessage as any;
                              const atts: any[] = Array.isArray(lm?.attachments) ? lm.attachments : [];
                              const total = atts.length;
                              if (total > 0) {
                                const imageCount = atts.filter(a => a?.type === 'image').length;
                                const videoCount = atts.filter(a => a?.type === 'video').length;
                                const pdfCount = atts.filter(a => a?.type === 'pdf').length;
                                const typesPresent = [imageCount > 0, videoCount > 0, pdfCount > 0].filter(Boolean).length;

                                let emoji = '📄';
                                let text: string;

                                if (typesPresent === 1) {
                                  if (imageCount > 0) {
                                    emoji = '📷';
                                    text = i18n.t(`messages.preview.image_${imageCount === 1 ? 'one' : 'other'}`, { count: imageCount }) as string;
                                  } else if (videoCount > 0) {
                                    emoji = '🎥';
                                    text = i18n.t(`messages.preview.video_${videoCount === 1 ? 'one' : 'other'}`, { count: videoCount }) as string;
                                  } else {
                                    emoji = '📄';
                                    text = i18n.t(`messages.preview.pdf_${pdfCount === 1 ? 'one' : 'other'}`, { count: pdfCount }) as string;
                                  }
                                } else {
                                  // Mixed types → generic attachments with total count
                                  emoji = '📄';
                                  text = i18n.t(`messages.preview.attachment_${total === 1 ? 'one' : 'other'}`, { count: total }) as string;
                                }

                                return <span>{emoji} {text}</span>;
                              }

                              // Handle special message types for translation
                              if (conversation.lastMessage.text === 'TIP_MESSAGE') {
                                return <span>{t('tipBuyer.conversationPreview', 'Property tip')}</span>;
                              }

                              // Translate system messages in conversation list preview.
                              // The backend emits viewer-specific variants (e.g. "You joined...")
                              // when it knows the current viewer is the actor/target of the event,
                              // and falls back to the generic variants otherwise.
                              const systemMessageMap: Record<string, string> = {
                                'A new participant joined the conversation': t('messages.system.previewParticipantAdded', 'A new participant joined'),
                                'You joined the conversation': t('messages.system.previewYouJoined', 'You joined the conversation'),
                                'A participant left the conversation': t('messages.system.previewParticipantLeft', 'A participant left'),
                                'You left the conversation': t('messages.system.previewYouLeft', 'You left the conversation'),
                                'A participant was removed from the conversation': t('messages.system.previewParticipantRemoved', 'A participant was removed'),
                                'You were removed from the conversation': t('messages.system.previewYouWereRemoved', 'You were removed from the conversation'),
                                'A new participant was invited to the conversation': t('messages.system.previewInviteSent', 'A new participant was invited'),
                                'You invited a new participant to the conversation': t('messages.system.previewYouInvited', 'You invited a new participant'),
                              };
                              const translatedText = systemMessageMap[conversation.lastMessage.text];
                              if (translatedText) {
                                return <span>{translatedText}</span>;
                              }

                              return conversation.lastMessage.text;
                            })()}
                          </div>
                        </div>
                        <div className="messages-modal__conversation-content-icon-open">
                          <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" focusable="false" style={
                            {
                              display: 'block', fill: 'none', height: '16px', width: '16px', stroke: 'currentcolor', strokeWidth: 4, overflow: 'visible',
                              transform: 'rotate(180deg)'
                            }
                          }>
                            <path d="m20 28-11.29289322-11.2928932c-.39052429-.3905243-.39052429-1.0236893 0-1.4142136l11.29289322-11.2928932"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            // Individual conversation view with guard until conversation data is available
            (() => {
              const activeConversation =
                conversations.find(c => c.id === selectedConversation) ||
                (pendingConversation?.id === selectedConversation
                  ? pendingConversation
                  : undefined);
              if (!activeConversation) {
                if (!isLoggedIn && onLoginClick) {
                  return (
                    <div className="messages-modal__login-button-container">
                      <button
                        className="messages-modal__login-button"
                        onClick={onLoginClick}
                      >
                        <Trans i18nKey="signupLoginModal.loginOrSignup">
                          Login or sign up
                        </Trans>
                      </button>
                    </div>
                  );
                }

                return (
                  <div className="messages-modal__loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <LoadingSpinner />
                  </div>
                );
              }
              return (
                <Conversation
                  conversationId={selectedConversation}
                  conversation={activeConversation}
                  messages={messages}
                  isLoading={isLoadingMessages}
                  newMessage={newMessage}
                  otherUserRole={activeConversation.otherUserRole}
                  setNewMessage={setNewMessage}
                  sendMessage={sendMessage}
                  goBack={() => { setSelectedConversation(null); setShowParticipantPanel(false); setShowInviteModal(false); }}
                  offMarketSellWillingnessId={activeConversation.offMarketSellWillingnessId}
                  offMarketBuyWillingnessId={activeConversation.offMarketBuyWillingnessId}
                  currentUserSide={activeConversation.currentUserSide}
                  hasViewedWelcome={activeConversation.hasViewedWelcome}
                  refreshConversations={refreshConversations}
                  onRefreshMessages={(invalidateReadStatus = false) => {
                    if (invalidateReadStatus === true && selectedConversation === selectedConversationRef.current) {
                      // A successful attachment send makes the displayed receipt stale,
                      // even if its following refresh fails. Pre-send polls are stale too.
                      messageAppliedSequence.current = ++messageFetchSequence.current;
                      setMessages(current => current.map(message => (
                        message.read === undefined ? message : { ...message, read: undefined }
                      )));
                    }
                    return fetchMessages(selectedConversation!, false);
                  }}
                  isJaakkoConversation={activeConversation.isJaakkoConversation}
                  showParticipantPanel={showParticipantPanel}
                  setShowParticipantPanel={setShowParticipantPanel}
                  showInviteModal={showInviteModal}
                  setShowInviteModal={setShowInviteModal}
                  onEndChat={handleEndChat}
                  onShowTemplate={() => setShowTemplateModal(true)}
                  onShowExpertHelp={() => setShowExpertHelp(true)}
                />
              );
            })()
          )}
        </div>

        {showEndChatConfirmation && (
          <div className="end-chat-confirmation-overlay">
            <div className="end-chat-confirmation">
              <div className="end-chat-confirmation__header">
                <h3>
                  <Trans i18nKey="messages.endChatConfirmTitle">
                    End chat
                  </Trans>
                </h3>
                <button
                  className="end-chat-confirmation__close"
                  onClick={cancelEndChat}
                >
                  <CloseIcon size={16} />
                </button>
              </div>
              <div className="end-chat-confirmation__content">
                <p>
                  <Trans i18nKey="messages.endChatConfirmText">
                    Are you sure you want to end this conversation? Once ended, neither you nor the other person will be able to send messages in this chat again.
                  </Trans>
                </p>
              </div>
              <div className="end-chat-confirmation__actions">
                <button
                  className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
                  onClick={cancelEndChat}
                  disabled={isEndingChat}
                >
                  <Trans i18nKey="messages.cancel">
                    Cancel
                  </Trans>
                </button>
                <button
                  className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
                  onClick={confirmEndChat}
                  disabled={isEndingChat}
                >
                  {isEndingChat ? (
                    <span className="end-chat-confirmation__loading-dots">
                      <Trans i18nKey="messages.ending">
                        Ending
                      </Trans>
                    </span>
                  ) : (
                    <Trans i18nKey="messages.confirm">
                      End chat
                    </Trans>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {showTemplateModal && createPortal(
          <div className="template-modal-overlay" onClick={() => setShowTemplateModal(false)}>
            <div className="template-modal" onClick={(e) => e.stopPropagation()}>
              <button
                className="template-modal__close"
                onClick={() => setShowTemplateModal(false)}
              >
                <CloseIcon size={16} />
              </button>

              <div className="template-modal__icon">
                <DocumentIcon />
              </div>

              <h2 className="template-modal__title">
                <Trans i18nKey="messages.templateModal.title">Deed of Sale Template</Trans>
              </h2>

              <p className="template-modal__subtitle">
                <Trans i18nKey="messages.templateModal.subtitle">
                  Download a professional template to help finalize your property transaction
                </Trans>
              </p>

              <div className="template-modal__features">
                <h4>
                  <Trans i18nKey="messages.templateModal.features.title">What's included:</Trans>
                </h4>
                <ul>
                  <li>
                    <span className="template-modal__feature-icon"><FeatureIcon type="parties" /></span>
                    <Trans i18nKey="messages.templateModal.features.parties">Buyer & seller details</Trans>
                  </li>
                  <li>
                    <span className="template-modal__feature-icon"><FeatureIcon type="property" /></span>
                    <Trans i18nKey="messages.templateModal.features.property">Property information</Trans>
                  </li>
                  <li>
                    <span className="template-modal__feature-icon"><FeatureIcon type="terms" /></span>
                    <Trans i18nKey="messages.templateModal.features.terms">Payment terms & conditions</Trans>
                  </li>
                  <li>
                    <span className="template-modal__feature-icon"><FeatureIcon type="signatures" /></span>
                    <Trans i18nKey="messages.templateModal.features.signatures">Signature fields</Trans>
                  </li>
                </ul>
              </div>

              <button
                className={`template-modal__download-button ${downloadState === 'success' ? 'template-modal__download-button--success' : ''}`}
                onClick={handleDownloadTemplate}
                disabled={downloadState === 'downloading'}
              >
                {downloadState === 'idle' && (
                  <>
                    <DownloadIcon />
                    <Trans i18nKey="messages.templateModal.downloadButton">Download Template</Trans>
                  </>
                )}
                {downloadState === 'downloading' && (
                  <>
                    <span className="template-modal__spinner" />
                    <Trans i18nKey="messages.templateModal.downloadingButton">Preparing...</Trans>
                  </>
                )}
                {downloadState === 'success' && (
                  <>
                    <CheckIcon />
                  </>
                )}
              </button>

              <p className="template-modal__file-info">
                <Trans i18nKey="messages.templateModal.fileInfo">Word document • Finnish legal format</Trans>
              </p>

              <p className="template-modal__disclaimer">
                <Trans i18nKey="messages.templateModal.disclaimer">
                  This template is provided as a starting point. For significant transactions, we recommend consulting a real estate lawyer.
                </Trans>
              </p>
            </div>
          </div>,
          document.body,
        )}

      </div>
      {showExpertHelp && (() => {
        const activeConv = selectedConversation ? conversations.find(c => c.id === selectedConversation) : null;
        const currentRole = activeConv
          ? resolveViewerMarketplaceRole(activeConv.currentUserSide, activeConv.otherUserRole)
          : 'UNKNOWN';
        // From a draft there is no thread yet, but the buyer side and the listing's address are known.
        const fromDraft = !activeConv && !!draftChat;
        return (
          <ExpertHelpSheet
            isVisible={showExpertHelp}
            onClose={() => setShowExpertHelp(false)}
            conversationId={selectedConversation}
            userRole={fromDraft ? 'BUYER' : currentRole}
            propertyAddress={activeConv ? activeConv.buildingInfo.address : draftChat?.title ?? ''}
            otherUserEmail={activeConv ? activeConv.otherUser.name : ''}
            isLoggedIn={isLoggedIn}
            onLoginClick={onLoginClick}
            onRequestSent={() => {
              if (selectedConversation) {
                fetchMessages(selectedConversation, false);
              }
              setShowExpertHelp(false);
            }}
          />
        );
      })()}
    </div>
  );
};

export default MessagesModal;
